
import { StudentRecord } from "../types";
import * as XLSX from "xlsx";

const COMMON_WORD_SURNAMES = new Set([
  'Low', 'High', 'Read', 'Long', 'Young', 'White', 'Green', 'Black', 'Brown', 
  'Hill', 'Page', 'Hand', 'Rice', 'May', 'Will', 'Day', 'King', 'Price', 
  'Bell', 'Cook', 'Ward', 'Wood', 'Hall', 'Major', 'Short', 'Smart', 'Sharp', 
  'Stone', 'Power', 'West', 'North', 'South', 'East', 'Field', 'Lake', 
  'Forest', 'Bridge', 'Bond', 'Case', 'Mark', 'Waters', 'Little', 'Small'
]);

export const parseTutorList = (base64: string): StudentRecord[] => {
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const workbook = XLSX.read(bytes.buffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    
    // Attempting range 5 as requested by UoN formatting
    const data: any[] = XLSX.utils.sheet_to_json(worksheet, { range: 5 });

    return data.map(row => {
      const id = String(row['ID number'] || '').trim();
      const surname = String(row['Surname'] || '').trim();
      const firstName = String(row['First Name'] || '').trim();
      const year = String(row['Year of Study'] || '').trim();
      const course = String(row['Course'] || '').trim();
      const fullName = `${firstName} ${surname}`.trim();
      
      return {
        name: fullName,
        id: id,
        year: year,
        course: course,
        _firstName: firstName,
        _surname: surname
      };
    }).filter(s => s.name || s.id);
  } catch (err) {
    console.error("Error parsing tutor list:", err);
    return [];
  }
};

export const redactText = (text: string, records: StudentRecord[]): { text: string, student: StudentRecord | null } => {
  let redacted = text;
  let matchedStudent: StudentRecord | null = null;

  // Step 0: Robust Email Redaction
  const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
  redacted = redacted.replace(emailRegex, '[EMAIL_REDACTED]');

  const validatedStudentIds = new Set<string>();

  // Pass 1: IDs and Full Names
  for (const record of records) {
    let studentDetected = false;
    const { id, name: fullName } = record;

    if (id && id.length >= 4) {
      const idPattern = new RegExp(`\\b${id.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'g');
      if (idPattern.test(redacted)) {
        redacted = redacted.replace(idPattern, '[ID_REDACTED]');
        studentDetected = true;
      }
    }

    if (fullName && fullName.length > 5) {
      const namePattern = new RegExp(`\\b${fullName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'gi');
      if (namePattern.test(redacted)) {
        redacted = redacted.replace(namePattern, '[NAME_REDACTED]');
        studentDetected = true;
      }
    }

    if (studentDetected) {
      validatedStudentIds.add(record.id);
      if (!matchedStudent) matchedStudent = record;
    }
  }

  // Pass 2: Surgical redaction for components
  for (const record of records) {
    const firstName = record._firstName;
    const surname = record._surname;
    const isAlreadyValidated = validatedStudentIds.has(record.id);

    if (surname && surname.length > 2) {
      const isCommonWord = COMMON_WORD_SURNAMES.has(surname);
      if (isCommonWord) {
        if (isAlreadyValidated) {
          const surnamePattern = new RegExp(`\\b${surname.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'g');
          redacted = redacted.replace(surnamePattern, '[SURNAME_REDACTED]');
        }
      } else {
        const surnamePattern = new RegExp(`\\b${surname.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'gi');
        if (surnamePattern.test(redacted)) {
          redacted = redacted.replace(surnamePattern, '[SURNAME_REDACTED]');
          if (!matchedStudent) matchedStudent = record;
        }
      }
    }

    if (firstName && firstName.length > 2) {
      const isCommonWord = COMMON_WORD_SURNAMES.has(firstName);
      if (isCommonWord) {
        if (isAlreadyValidated) {
          const firstNamePattern = new RegExp(`\\b${firstName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'g');
          redacted = redacted.replace(firstNamePattern, '[GIVEN_NAME_REDACTED]');
        }
      } else {
        const firstNamePattern = new RegExp(`\\b${firstName.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'gi');
        if (firstNamePattern.test(redacted)) {
          redacted = redacted.replace(firstNamePattern, '[GIVEN_NAME_REDACTED]');
          if (!matchedStudent) matchedStudent = record;
        }
      }
    }
  }

  return { text: redacted, student: matchedStudent };
};
